import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Loader from '../../../../../../components/Loader'
import { Dialog } from '../../../../../../components/Popup'
import Toast from '../../../../../../components/Toast'
import API from '../../../../../../lib/api'
import { getMessage } from '../../../../../../lib/translator'
import { formatIsoDate } from '../../../../../../lib/datetime'
import {
  getPickingOrderStatusClassName,
  getPickingOrderStatusLabel,
  mapPickingStatusToDeliveryOrderStatus,
} from '../../../../DeliveryOrdersPicking/constants/status'
import { getPickingOrderSourceClassName } from '../../../../DeliveryOrdersPicking/constants/source'
import { getOrderSourceLabel } from '../../../constants/orderSource'
import {
  formatOrderSlotDate,
  formatOrderSlotTime,
} from '../../../../DeliveryOrdersPicking/utils/displayFormatters'
import { isAmendedOrder } from '../../../../DeliveryOrdersPicking/utils/isAmendedOrder'
import PacklistView from '../../PacklistView'
import PacklistPackingDetails from '../../PackingDetails'
import PoaGroupedPacklists from './PoaGroupedPacklists'
import { buildDspBackOfficeUrl } from '../utils/buildDspBackOfficeUrl'
import {
  applyCratesEdit,
  applyItemQuantityEdit,
  getOverPickingErrorMessage,
  saveEditedPacklists,
} from '../DspOrderActions/editPacklistState'
import './style.scss'

const DetailField = ({ label, children, className = '' }) => (
  <div className={`dsp-order-detail__field ${className}`.trim()}>
    <dt className="dsp-order-detail__label">{label}</dt>
    <dd className="dsp-order-detail__value">{children}</dd>
  </div>
)

const ExternalLinkIcon = () => (
  <svg
    className="dsp-order-detail__external-icon"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M14 3h7v7h-2V6.4l-9.3 9.3-1.4-1.4L17.6 5H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
    />
  </svg>
)

const DspPickingOrderDetails = ({
  order,
  packlistData,
  packlistGroups = [],
  packlistLoading = false,
  currency,
  listPath = '/operations/picking-delivery-orders',
  onReload,
  isEditingPacked = false,
  onStopEditPacked,
}) => {
  const dspBackOfficeUrl = buildDspBackOfficeUrl(order)
  const packlists = packlistData || []
  const deliveryOrderStatus = mapPickingStatusToDeliveryOrderStatus(
    order?.status
  )
  // The opened order's place in its POA family, once the linked groups have loaded.
  const amendmentNumber =
    packlistGroups.find((group) => group.isOwn)?.amendmentNumber || null

  const [editedPacklists, setEditedPacklists] = useState({})
  const [saving, setSaving] = useState(false)
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    message: null,
    isWarning: false,
  })
  const [showSaveToast, setShowSaveToast] = useState(false)
  const packlistsSectionRef = useRef(null)

  useEffect(() => {
    if (!isEditingPacked) {
      setEditedPacklists({})
      setSaving(false)
      return
    }
    setEditedPacklists({})
    setErrorDialog({ show: false, message: null, isWarning: false })
    packlistsSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [isEditingPacked])

  const cancelEditPacked = () => {
    setEditedPacklists({})
    setSaving(false)
    setErrorDialog({ show: false, message: null, isWarning: false })
    onStopEditPacked?.()
  }

  const showError = useCallback((message, { isWarning = false } = {}) => {
    setErrorDialog({ show: true, message, isWarning })
    setSaving(false)
  }, [])

  const handleItemQuantity = useCallback(
    (packlistId, item, itemQuantity, overPicked) => {
      if (overPicked) {
        showError(getOverPickingErrorMessage(item, itemQuantity, getMessage), {
          isWarning: true,
        })
        return
      }
      setEditedPacklists((prev) =>
        applyItemQuantityEdit(prev, packlistId, item, itemQuantity)
      )
    },
    [showError]
  )

  const handleCrates = useCallback((values, oriValues) => {
    setEditedPacklists((prev) => applyCratesEdit(prev, values, oriValues))
  }, [])

  const handleSavePacked = async () => {
    if (Object.keys(editedPacklists).length === 0 || saving) {
      return
    }
    setSaving(true)
    try {
      await saveEditedPacklists(editedPacklists, (url) => new API({ url }))
      setSaving(false)
      setEditedPacklists({})
      setShowSaveToast(true)
      onStopEditPacked?.()
      onReload?.()
    } catch (error) {
      showError(error?.message || getMessage('error.server'))
    }
  }

  const closeError = () => {
    setErrorDialog({ show: false, message: null, isWarning: false })
  }

  const isNotEdited = Object.keys(editedPacklists).length === 0

  const backOfficeCta = dspBackOfficeUrl ? (
    <a
      className="dsp-order-detail__backoffice-link"
      href={dspBackOfficeUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {getMessage('picking.orderDetail.dsp.openInBackOffice')}
      <ExternalLinkIcon />
    </a>
  ) : (
    <span className="dsp-order-detail__backoffice-link dsp-order-detail__backoffice-link--disabled">
      {getMessage('picking.orderDetail.dsp.openInBackOffice')}
    </span>
  )

  return (
    <div className="dsp-order-detail">
      <div
        className="dsp-order-detail__page-header"
        data-testid="dsp-page-header"
      >
        <div className="dsp-order-detail__page-header-left">
          {order?.status ? (
            <span
              className={`dsp-order-detail__status-pill ${getPickingOrderStatusClassName(
                order.status
              )}`}
              data-testid="dsp-header-status"
            >
              {getPickingOrderStatusLabel(order.status) || order.status}
            </span>
          ) : null}
          {order?.source ? (
            <span
              className={`dsp-order-detail__source-pill ${getPickingOrderSourceClassName(
                order.source
              )}`}
            >
              {getOrderSourceLabel(order.source)}
            </span>
          ) : null}
          {isAmendedOrder(order) ? (
            <span
              className="dsp-order-detail__amended-pill"
              data-testid="dsp-header-amended"
            >
              {amendmentNumber
                ? getMessage('picking.orderDetail.amendment.numbered', {
                    number: amendmentNumber,
                  })
                : getMessage('picking.orderDetail.amended.badge')}
            </span>
          ) : null}
        </div>
        <div className="dsp-order-detail__page-header-right">
          {backOfficeCta}
        </div>
      </div>

      <div className="dsp-order-detail__banner">
        <span className="dsp-order-detail__banner-icon" aria-hidden="true">
          i
        </span>
        <p>{getMessage('picking.orderDetail.dsp.banner')}</p>
      </div>

      <section
        className="dsp-order-detail__card"
        aria-labelledby="dsp-order-summary-heading"
      >
        <div className="dsp-order-detail__card-header">
          <h2
            id="dsp-order-summary-heading"
            className="dsp-order-detail__card-title"
          >
            {getMessage('picking.orderDetail.dsp.summary.title')}
          </h2>
        </div>

        <div className="dsp-order-detail__groups">
          <div className="dsp-order-detail__group">
            <h3 className="dsp-order-detail__group-title">
              {getMessage('picking.orderDetail.dsp.summary.group.order')}
            </h3>
            <dl className="dsp-order-detail__group-grid">
              <DetailField
                label={getMessage(
                  'picking.orderDetail.table.header.referenceNumber'
                )}
              >
                {order?.referenceNumber || '-'}
              </DetailField>
              <DetailField
                label={getMessage(
                  'picking.orderDetail.table.header.childOrderId'
                )}
              >
                {order?.childOrderId || '-'}
              </DetailField>
            </dl>
          </div>

          <div className="dsp-order-detail__group">
            <h3 className="dsp-order-detail__group-title">
              {getMessage('picking.orderDetail.dsp.summary.group.schedule')}
            </h3>
            <dl className="dsp-order-detail__group-grid">
              <DetailField
                label={getMessage(
                  'picking.orderDetail.table.header.preferredDate'
                )}
              >
                {formatOrderSlotDate(order?.preferredDate) || '-'}
              </DetailField>
              <DetailField
                label={getMessage('picking.orderDetail.table.header.slot')}
              >
                {formatOrderSlotTime(
                  order?.slotStartTime,
                  order?.slotEndTime
                ) || '-'}
              </DetailField>
              {order?.placedOn ? (
                <DetailField
                  label={getMessage('picking.orderDetail.dsp.placedOn')}
                >
                  {formatIsoDate(order.placedOn) || order.placedOn}
                </DetailField>
              ) : null}
            </dl>
          </div>

          {(order?.sapStoreId || order?.storeId) && (
            <div className="dsp-order-detail__group">
              <h3 className="dsp-order-detail__group-title">
                {getMessage('picking.orderDetail.dsp.summary.group.store')}
              </h3>
              <dl className="dsp-order-detail__group-grid">
                {order?.sapStoreId ? (
                  <DetailField
                    label={getMessage('picking.orderDetail.dsp.store')}
                  >
                    {order.sapStoreId}
                  </DetailField>
                ) : null}
                {order?.storeId ? (
                  <DetailField
                    label={getMessage('picking.orderDetail.dsp.storeId')}
                  >
                    {order.storeId}
                  </DetailField>
                ) : null}
              </dl>
            </div>
          )}
        </div>
      </section>

      <section
        ref={packlistsSectionRef}
        className="dsp-order-detail__packlists"
        aria-labelledby="dsp-order-packlists-heading"
        data-testid="dsp-packlists-section"
      >
        <h2
          id="dsp-order-packlists-heading"
          className="dsp-order-detail__section-title"
        >
          {getMessage('picking.orderDetail.dsp.packlists.title')}
        </h2>
        {packlistLoading ? (
          <Loader />
        ) : packlists.length === 0 ? (
          <p className="dsp-order-detail__empty-packlists">
            {getMessage('order.details.noPacklist')}
          </p>
        ) : (
          <>
            <PacklistView
              data={{
                packlistData: packlists,
                clickAndCollectCharges: 0,
                orderItems: [],
                shipping: 0,
                invoiceAmount: 0,
                amount: 0,
                pendingAmount: 0,
                type: '',
                serviceFee: 0,
              }}
              isOperationsPage
              isSplitOrder={false}
              currency={currency}
              status={deliveryOrderStatus}
              isDfOrder={false}
              isDonationOrder={false}
              isDsp
              itemEditMode={isEditingPacked}
              updateItemQuantity={handleItemQuantity}
            />
            <PacklistPackingDetails
              packlistDetails={packlists}
              itemEditMode={isEditingPacked}
              updateCrates={handleCrates}
            />
            {isEditingPacked ? (
              <div
                className="dsp-order-detail__edit-footer"
                data-testid="dsp-inline-edit-footer"
              >
                <button
                  type="button"
                  className="button"
                  onClick={cancelEditPacked}
                  disabled={saving}
                >
                  {getMessage('order.details.itemsTable.actions.cancelText')}
                </button>
                <button
                  type="button"
                  className="primary button"
                  onClick={handleSavePacked}
                  disabled={isNotEdited || saving}
                  data-testid="dsp-inline-edit-save"
                >
                  {getMessage('order.details.itemsTable.actions.confirmText')}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      {/* Outside the packlists section on purpose: that section short-circuits when this
          order has no packlists of its own, but a linked order's items must still show. */}
      <PoaGroupedPacklists
        groups={packlistGroups}
        currency={currency}
        orderStatus={deliveryOrderStatus}
        loading={packlistLoading}
        basePath={listPath}
      />

      <div className="dsp-order-detail__actions">
        <Link className="dsp-order-detail__back-link" to={listPath}>
          {getMessage('picking.orderDetail.dsp.backToList')}
        </Link>
      </div>

      {errorDialog.show ? (
        <Dialog
          title={getMessage('order.details.edit-packed.error.dialog.label')}
          show={errorDialog.show}
          information={errorDialog.message}
          close={closeError}
          closeText={getMessage('order.details.edit-packed.error.closeDialog')}
        />
      ) : null}

      {showSaveToast ? (
        <Toast
          content={getMessage(
            'picking.orderDetail.dsp.actions.editPacked.success'
          )}
          cancelMessage={getMessage('dialog.okText')}
          timer={4000}
          toastColor="#333333"
          onClose={() => setShowSaveToast(false)}
        />
      ) : null}
    </div>
  )
}

export default DspPickingOrderDetails
